import type { ContentLocale, ContentRiskLevel } from "./content";

export const INTELLIGENCE_MARKETS = [
  "AE",
  "SA",
  "QA",
  "KW",
  "BH",
  "OM",
  "GLOBAL",
] as const;

export type IntelligenceMarket = (typeof INTELLIGENCE_MARKETS)[number];

export const INTELLIGENCE_PROVIDERS = [
  "sentry",
  "posthog",
  "dataforseo",
  "search_console",
  "tabbio",
  "official_source",
] as const;

export type IntelligenceProvider = (typeof INTELLIGENCE_PROVIDERS)[number];

export const INTELLIGENCE_OPPORTUNITY_STATUSES = [
  "new",
  "reviewing",
  "accepted",
  "dismissed",
  "completed",
] as const;

export type IntelligenceOpportunityStatus =
  (typeof INTELLIGENCE_OPPORTUNITY_STATUSES)[number];

export const INTELLIGENCE_OPPORTUNITY_KINDS = [
  "blog",
  "changelog",
  "documentation",
  "report",
  "product_fix",
  "measurement_fix",
  "seo_content",
  "market_content",
  "competitor_response",
] as const;

export type IntelligenceOpportunityKind =
  (typeof INTELLIGENCE_OPPORTUNITY_KINDS)[number];

export interface IntelligenceEvidence {
  provider: IntelligenceProvider;
  sourceId?: string;
  sourceUrl?: string;
  title: string;
  summary: string;
  observedAt: string;
  metrics?: Record<string, unknown>;
}

export interface IntelligenceScoreBreakdown {
  searchDemand: number;
  firstPartyDemand: number;
  competitorGap: number;
  conversionRelevance: number;
  topicalAuthority: number;
  regionalTiming: number;
  evidenceReadiness: number;
}

export interface IntelligenceSignalRecord {
  id: string;
  fingerprint: string;
  provider: IntelligenceProvider | null;
  type: string;
  market: IntelligenceMarket;
  locale: ContentLocale | null;
  title: string;
  summary: string;
  severity: "info" | "warning" | "critical";
  confidence: number;
  evidence: IntelligenceEvidence[];
  entityType: string | null;
  entityId: string | null;
  observedAt: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntelligenceOpportunityRecord {
  id: string;
  fingerprint: string;
  market: IntelligenceMarket;
  locale: ContentLocale;
  kind: IntelligenceOpportunityKind;
  status: IntelligenceOpportunityStatus;
  title: string;
  summary: string;
  primaryKeyword: string | null;
  score: number;
  scoreBreakdown: IntelligenceScoreBreakdown;
  confidence: number;
  riskLevel: ContentRiskLevel;
  evidence: IntelligenceEvidence[];
  recommendedAction: string;
  contentDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntelligenceSourceRecord {
  id: string;
  key: string;
  provider: IntelligenceProvider;
  displayName: string;
  enabled: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntelligenceOverview {
  generatedAt: string;
  markets: Array<{
    market: IntelligenceMarket;
    signalCount: number;
    opportunityCount: number;
    criticalCount: number;
  }>;
  topOpportunities: IntelligenceOpportunityRecord[];
  criticalSignals: IntelligenceSignalRecord[];
  sources: IntelligenceSourceRecord[];
}
